import { Component, OnInit } from '@angular/core';
import { InputTextModule } from 'primeng/inputtext';
import { AuthService } from '../../services/auth.service';
import { authUser } from '../../interfaces/auth/auth.model';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DirectionService } from '../../services/direction.service';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { NavTitlePipe } from '../../pipes/nav-title.pipe';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [TranslateModule, InputTextModule, FormsModule, ReactiveFormsModule, CommonModule, NavTitlePipe],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss'
})
export class ProfileComponent implements OnInit {
  user!: authUser;
  dir!: 'rtl' | 'ltr';

  isEditModalVisible = false;
  isPasswordModalVisible = false;
  isSubmitting = false;
  isPasswordSubmitting = false;
  currentPasswordFieldType: 'password' | 'text' = 'password';
  newPasswordFieldType: 'password' | 'text' = 'password';
  confirmPasswordFieldType: 'password' | 'text' = 'password';

  profileForm = new FormGroup({
    name: new FormControl('', Validators.required),
    email: new FormControl('', [Validators.required, Validators.email]),
    job_title: new FormControl(''),
  });

  passwordForm = new FormGroup({
    current_password: new FormControl('', Validators.required),
    new_password: new FormControl('', [Validators.required, Validators.minLength(6)]),
    confirm_password: new FormControl('', Validators.required),
  });

  constructor(
    private authService: AuthService,
    private directionService: DirectionService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.directionService.direction$.subscribe((dir) => {
      this.dir = dir;
    });
    this.authService.authBufferSub$.subscribe((user) => {
      this.user = user as authUser;
    });
  }

  goBack() {
    window.history.back();
  }

  openEditModal() {
    if (!this.user) return;
    this.profileForm.reset({
      name: this.user.name || '',
      email: this.user.email || '',
      job_title: this.user.job_title || '',
    });
    this.isEditModalVisible = true;
  }

  closeEditModal() {
    this.isEditModalVisible = false;
    this.isSubmitting = false;
  }

  openPasswordModal() {
    this.passwordForm.reset({
      current_password: '',
      new_password: '',
      confirm_password: '',
    });
    this.currentPasswordFieldType = 'password';
    this.newPasswordFieldType = 'password';
    this.confirmPasswordFieldType = 'password';
    this.isPasswordModalVisible = true;
  }

  closePasswordModal() {
    this.isPasswordModalVisible = false;
    this.isPasswordSubmitting = false;
  }

  togglePasswordVisibility(field: 'current' | 'new' | 'confirm') {
    if (field === 'current') {
      this.currentPasswordFieldType = this.currentPasswordFieldType === 'password' ? 'text' : 'password';
      return;
    }
    if (field === 'new') {
      this.newPasswordFieldType = this.newPasswordFieldType === 'password' ? 'text' : 'password';
      return;
    }
    this.confirmPasswordFieldType = this.confirmPasswordFieldType === 'password' ? 'text' : 'password';
  }

  submitProfile() {
    if (this.profileForm.invalid || !this.user) {
      this.profileForm.markAllAsTouched();
      this.toastr.error(
        this.dir === 'rtl' ? 'يرجى مراجعة البيانات' : 'Please review the form',
        this.dir === 'rtl' ? 'فشل' : 'Failed'
      );
      return;
    }

    this.isSubmitting = true;
    const payload = {
      name: (this.profileForm.value.name || '').trim(),
      email: (this.profileForm.value.email || '').trim(),
      job_title: (this.profileForm.value.job_title || '').trim(),
    };

    this.authService.updateOwnProfile(payload).subscribe({
      next: () => {
        this.authService.refreshLocalProfile({
          name: payload.name,
          email: payload.email,
          job_title: payload.job_title,
        });
        this.toastr.success(
          this.dir === 'rtl' ? 'تم تحديث الملف الشخصي بنجاح' : 'Profile updated successfully',
          this.dir === 'rtl' ? 'عملية ناجحة' : 'Success'
        );
        this.closeEditModal();
      },
      error: (err) => {
        this.isSubmitting = false;
        const message =
          err?.error?.error?.message ||
          err?.error?.Error?.message ||
          err?.error?.message ||
          (this.dir === 'rtl' ? 'فشل تحديث الملف الشخصي' : 'Failed to update profile');
        this.toastr.error(message, this.dir === 'rtl' ? 'فشل' : 'Failed');
      }
    });
  }

  submitPassword() {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      this.toastr.error(
        this.dir === 'rtl' ? 'يرجى مراجعة البيانات' : 'Please review the form',
        this.dir === 'rtl' ? 'فشل' : 'Failed'
      );
      return;
    }

    const current = this.passwordForm.value.current_password || '';
    const next = this.passwordForm.value.new_password || '';
    const confirm = this.passwordForm.value.confirm_password || '';

    if (next !== confirm) {
      this.toastr.error(
        this.dir === 'rtl' ? 'كلمة المرور الجديدة غير متطابقة' : 'New passwords do not match',
        this.dir === 'rtl' ? 'فشل' : 'Failed'
      );
      return;
    }

    this.isPasswordSubmitting = true;
    this.authService.changeOwnPassword({
      current_password: current,
      new_password: next,
    }).subscribe({
      next: () => {
        this.toastr.success(
          this.dir === 'rtl' ? 'تم تغيير كلمة المرور بنجاح' : 'Password changed successfully',
          this.dir === 'rtl' ? 'عملية ناجحة' : 'Success'
        );
        this.closePasswordModal();
      },
      error: (err) => {
        this.isPasswordSubmitting = false;
        const message =
          err?.error?.error?.message ||
          err?.error?.Error?.message ||
          err?.error?.message ||
          (this.dir === 'rtl' ? 'فشل تغيير كلمة المرور' : 'Failed to change password');
        this.toastr.error(message, this.dir === 'rtl' ? 'فشل' : 'Failed');
      }
    });
  }
}
